import React from 'react';
import {
    Chart as ChartJS, 
    LinearScale, 
    LineElement,
    PointElement, 
    Tooltip,
    TimeScale,
    TimeSeriesScale
} from "chart.js"; 
import { Scatter } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import annotationPlugin from 'chartjs-plugin-annotation';
import './SceneSessionChart.css'
import PropTypes from "prop-types";
import {convertSecondToTimeLabel, convertSecondToTimeLabelForFileName} from '../../common/utils/date_utils'

ChartJS.register(LinearScale, LineElement, PointElement, Tooltip, TimeScale, TimeSeriesScale, annotationPlugin);

let ttElemPtr = undefined;
let mouseOverToolTip = false;
let TooltipStateStatedByChart = false;
let tooltip_dataset_exclusion_list = ["Markers", "Image_markers"];


// -----------------------------------------------------------------------------
const getOrCreateTooltip = (chart) => {
    let tooltipEl = chart.canvas.parentNode.querySelector('div');

    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        //tooltipEl.style.background = 'rgba(0, 0, 0, 0.7)';
        tooltipEl.style.background = 'rgba(80, 80, 80, 1)';
        tooltipEl.style.borderRadius = '3px';
        tooltipEl.style.color = 'white';
        tooltipEl.style.opacity = 1;
        tooltipEl.style.display = 'block';
        //tooltipEl.style.pointerEvents = 'none';
        tooltipEl.style.position = 'absolute';
        tooltipEl.style.transform = 'translate(-50%, 0)';
        tooltipEl.style.transition = 'all .1s ease';
        tooltipEl.style.zIndex = '100';

        const ttRoot = document.createElement('div');
        ttRoot.id = 'tooltip_root';

        tooltipEl.appendChild(ttRoot);
        chart.canvas.parentNode.appendChild(tooltipEl);
        ttElemPtr = tooltipEl;

        tooltipEl.onmouseover = () => {
            mouseOverToolTip = true;
        }
        tooltipEl.onmouseleave = () => {
            mouseOverToolTip = false;
            mouseLeaveEvent();
        }
    }

    return tooltipEl;
};

const hideTooltip = () => {
    if (ttElemPtr) {
        ttElemPtr.style.opacity = 0;
        // When only opacity is changed, mouse event are still intercepted by the component
        ttElemPtr.style.pointerEvents = 'none';
    }
}

const get_tooltip_visibility = () => {
    if (ttElemPtr)
        return (ttElemPtr.style.opacity !== 0);

    return false;
}

const mouseLeaveEvent = () => {
    if (!get_tooltip_visibility())
        return;

    const tooltip_close_delay = 200 // ms
    setTimeout(() => {
        if (!mouseOverToolTip && TooltipStateStatedByChart === 0)  hideTooltip();
    }, tooltip_close_delay);
}

function is_tooltip_related_to_excluded_dataset(tooltip_body_line) {
    for (let item of tooltip_dataset_exclusion_list) {
        if (tooltip_body_line.startsWith(item))
            return true;
    }
    return false;
}

function get_closest_scene_image_info(p_time, datasets) {
    let markers_dataset = undefined;
    const image_time_diff_threshold = 10000; // milliseconds
    // Find Image markers dataset
    for (let dataset of datasets)
        if (dataset.label === "Image_markers")
            markers_dataset = dataset;

    // Find the closest data point from image markers array. It does not currently make a distinction between past or next datapoint.
    // Note: One may prefer to select image from past data point over next one if 2 data point are equidistant.
    if (markers_dataset) {
        let closest_datapoint = undefined;
        let closest_point_time_diff = Number.MAX_VALUE;
        for (let item of markers_dataset.data) {
            let time_diff = Math.abs(item[0] - p_time);
            if (time_diff < closest_point_time_diff) {
                closest_datapoint = item;
                closest_point_time_diff = time_diff;
            }
        }
        if (closest_datapoint && closest_point_time_diff < image_time_diff_threshold) {
            // [SceneImage]_at_[00m2.63s]
            const filename = "SceneImage_at_" + convertSecondToTimeLabelForFileName(closest_datapoint[0]/1000) + ".png";
            return {image_data: closest_datapoint[2], image_filename: filename};
        }
    }

    return {image_data: null, image_filename: ""};
}


const externalTooltipHandler = (context) => {
    // Tooltip Element
    const {chart, tooltip} = context;
    const tooltipEl = getOrCreateTooltip(chart);

    // Hide if no tooltip
    TooltipStateStatedByChart = tooltip.opacity;
    if (tooltip.opacity === 0) {
        // Here is the place the tooltip is usually removed when no intersection occur
        //tooltipEl.style.opacity = 0;
        //tooltipEl.style.pointerEvents = 'none';
        mouseLeaveEvent();
        return;
    }

    // Set Tooltip content
    if (tooltip.body) {
        // Exclude tooltips for Markers
        const bodyLines = tooltip.body.map(b => b.lines.filter(line => !is_tooltip_related_to_excluded_dataset(line)));
        // console.debug(bodyLines);
        if (bodyLines[0].length === 0) return;

        // Add custom tooltip header
        const xValue = tooltip.dataPoints[0].parsed.x;
        const tt_Head = document.createElement('div');
        tt_Head.style.display = 'flex';
        {
            const tt_title = document.createElement('span');
            const text = document.createTextNode("Time: " + convertSecondToTimeLabel(xValue / 1000));
            tt_title.appendChild(text);
            tt_title.style.display = 'flex';
            tt_title.style.flexGrow = '1';
            tt_title.style.justifyContent = 'center';
            tt_title.style.alignItems = 'center';
            tt_title.style.textAlign = 'center';
            tt_title.style.marginLeft = '2em'; // To compensate for the button on the right side
            tt_title.style.marginRight = '1em';

            let close_btn = document.createElement('button');
            close_btn.innerHTML = '<i class="fa fa-solid fa-times" aria-hidden="true"></i>';
            close_btn.onclick = (e) => {
                e.stopPropagation();
                e.preventDefault();
                hideTooltip();
            }

            tt_Head.appendChild(tt_title);
            tt_Head.appendChild(close_btn);
        }

        // ---------
        // Add image and download button
        const {image_data, image_filename} = get_closest_scene_image_info(xValue, chart.config._config.data.datasets);
        const img_div = document.createElement('div');
        const btn_div = document.createElement("div");

        if (image_data) {
            const img = document.createElement('img');
            img.alt = "Scene screenshot";
            img.style.height = "200px";
            //img.width="150px";
            //img.style.width = "400px";
            //img.setAttribute('width', '100%');
            img.onerror = function () {
                this.src = "/images/icons8-image-100.png"; // replace by an error.png image instead
            };
            img.src = image_data;

            img_div.style.backgroundColor = 'inherit';
            img_div.style.borderWidth = 0;
            img_div.style.marginBottom = '.5em';
            img_div.appendChild(img);

            // ----------------------
            // download button
            btn_div.style.display = "flex";
            btn_div.style.justifyContent = 'center';
            btn_div.style.alignItems = 'center';
            btn_div.style.textAlign = 'center';
            {
                const btn = document.createElement("button");
                btn.className = "app-button-style2 active";
                btn.style.cursor = 'pointer';
                btn.textContent = "Download";
                btn.onclick = (e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    let a = document.createElement("a");
                    a.href = image_data;
                    a.download = image_filename; //[SceneName]_at_[00m2.63s]
                    a.click(); // Download file
                }
                btn_div.appendChild(btn);
            }
        }
        //------------
        // console.debug(tooltip.dataPoints[0].dataset.label); // dataset title
        // console.debug(tooltip.dataPoints[0].raw); // Point hits

        // Remove old children
        const ttRoot = tooltipEl.querySelector('div');
        while (ttRoot.firstChild) {
            ttRoot.firstChild.remove();
        }

        // Add new children
        ttRoot.appendChild(tt_Head);
        ttRoot.appendChild(img_div);
        ttRoot.appendChild(btn_div);
    }

    const {offsetLeft: positionX, offsetTop: positionY} = chart.canvas;

    // Display, position, and set styles for font
    tooltipEl.style.opacity = 1;
    tooltipEl.style.display = 'auto';
    tooltipEl.style.pointerEvents = 'auto';
    tooltipEl.style.left = positionX + tooltip.caretX + 'px';
    tooltipEl.style.top = positionY + tooltip.caretY + 5 + 'px'; // bellow the cursor
    tooltipEl.style.font = tooltip.options.bodyFont.string;
    tooltipEl.style.padding = tooltip.options.padding + 'px ' + tooltip.options.padding + 'px';
};

// -----------------------------------------------------------------------------
const scatterPlotStyle = {
    showLine: true,
    backgroundColor: 'grey',
    borderColor: '#FF2E2E52',
    pointBorderColor: 'grey',
    pointBackgroundColor: 'grey',
    pointBorderWidth: 1,
    pointHoverRadius: 5,
    pointHoverBackgroundColor: 'blue',
    pointHoverBorderColor: 'blue',
    pointHoverBorderWidth: 0,
    pointRadius: 2,
    pointHitRadius: 5,

    markersColor: '#408d1a',
    markersRadius: '8',
    markersFontSize: '12',
    markersPointStyle: 'circle', // chartjs point style

    imageMarkersColor: 'rgba(52,63,229,0.76)',
    imageMarkersRadius: '6',
    imageMarkersPointStyle: false, // 'triangle',

    xAxisBorderWidth: 2,
    xAxisBorderColor: 'rgb(180,180,180)',

    yAxisBorderWidth: 0,
    yAxisBorderColor: 'rgb(180,180,180)',

    horizontalLineColor: 'rgb(135,135,135, .5)',
    horizontalLineWidth: 1,
};
// -----------------------------------------------------------------------------
export class SceneSessionChartComponent extends React.Component {

    static propTypes = {
        title: PropTypes.string,
        datasets: PropTypes.array.isRequired,
        xAxisTitle: PropTypes.string,
        yAxisTitle: PropTypes.string,
        xMin: PropTypes.number,
        xMax: PropTypes.number,
        yMin: PropTypes.number,
        yMax: PropTypes.number,
    };

    // ---------------
    render() {
        let {datasets, title, xAxisTitle, yAxisTitle, xMin, xMax, yMin, yMax} = this.props
        const spanX = xMax - xMin
        const spanY = yMax - yMin
        if (spanX < 1E-6)  xMax = xMin + 1;
        if (spanY < 1E-6)  yMax = yMin + 1;
        const yMedianValue = yMin + spanY * 0.5;
        const stepSizeX = (spanX) < 10000 ? 1 : (spanX < 60000) ? 5 : 15
        const stepSizeY = 2 * Math.pow(10, Math.ceil(Math.log10(spanY))-1)

        this.chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            title: {
                display: (title.length > 0),
                text: title
            },
            plugins: {
                legend: {
                    display: false
                },
                tooltip: {
                    enabled: false,
                    position: 'nearest',
                    external: externalTooltipHandler
                },
                annotation: {
                    annotations: {
                        horizontalLine: {
                            type: 'line',
                            yMin: yMedianValue,
                            yMax: yMedianValue,
                            borderColor: scatterPlotStyle.horizontalLineColor,
                            borderWidth: scatterPlotStyle.horizontalLineWidth,
                        }
                    }
                }
            },
            scales: {
                x: {
                    display: true,
                    suggestedMin: xMin,
                    suggestedMax: xMax,
                    type: 'timeseries',  // If provided x values are float, milliseconds are expected
                    time: {
                        unit: 'second',
                        displayFormats: {
                            second: 'mm:ss',
                            minute: 'mm:ss',
                            day: 'DD'
                        }
                    },
                    title: {
                        display: false,
                        text: xAxisTitle
                    },
                    border:{
                        display: (scatterPlotStyle.xAxisBorderWidth > 0),
                        dash: ["8", '4'],
                        color: scatterPlotStyle.xAxisBorderColor,
                        width: scatterPlotStyle.xAxisBorderWidth,
                    },
                    ticks:{stepSize: stepSizeX}
                },
                y: {
                    display: false,
                    suggestedMin: 0,
                    suggestedMax: 100,  // Standardized Y range for everything, but the time series, which have their own axis & scale
                    title: {
                        display: false,
                        text: yAxisTitle
                    },
                    border: {
                        display: (scatterPlotStyle.yAxisBorderWidth > 0),
                        color: scatterPlotStyle.yAxisBorderColor,
                        width: scatterPlotStyle.yAxisBorderWidth,
                    },
                    ticks:{display: true, stepSize: stepSizeY}
                }
            }
        };

        this.datasetsToPlot = [];
        let ppg_range = [1E12, -1E12];
        for (let dataset of datasets) {
            let chartDataset;
            if (dataset.title ===  "ppg") {
                chartDataset = {
                    showLine: scatterPlotStyle.showLine,
                    pointBorderColor: scatterPlotStyle.pointBorderColor,
                    pointBackgroundColor: scatterPlotStyle.pointBackgroundColor,
                    pointBorderWidth: scatterPlotStyle.pointBorderWidth,
                    pointHoverRadius: scatterPlotStyle.pointHoverRadius,
                    pointHoverBackgroundColor: scatterPlotStyle.pointHoverBackgroundColor,
                    pointHoverBorderColor: scatterPlotStyle.pointHoverBorderColor,
                    pointHoverBorderWidth: scatterPlotStyle.pointHoverBorderWidth,
                    pointStyle: false,
                    pointRadius: scatterPlotStyle.pointRadius,
                    pointHitRadius: scatterPlotStyle.pointHitRadius,
                    borderColor: scatterPlotStyle.borderColor,
                    data: dataset.data,
                    label: dataset.title,
                    yAxisID: "ppg"
                };
                this.datasetsToPlot.push(chartDataset);
                // for (let data of dataset.data) {
                //     let ppg_value = data[1];
                //     if (ppg_value > ppg_range[1])  ppg_range[1] = ppg_value;
                //     if (ppg_value < ppg_range[0])  ppg_range[0] = ppg_value;
                // }
                ppg_range[0] = yMin
                ppg_range[1] = yMax
                ppg_range[0] = ppg_range[0] < 0 ? (ppg_range[0] * 1.25) : (ppg_range[0] * 0.75)
                ppg_range[1] = ppg_range[1] > 0 ? (ppg_range[1] * 1.25) : (ppg_range[1] * 0.75)
                // Adjusting ppg axis
                // Note: Named axis configuration can only be set if at least 1 dataset refer to it otherwise
                // the named axis isn't created and an error is raised when chartJs tries to configure it.
                this.chartOptions.scales.ppg = {
                    display: true,
                    suggestedMin: ppg_range[0],
                    suggestedMax: ppg_range[1],
                    title: {
                        display: true
                    },
                    border: {
                        display: (scatterPlotStyle.yAxisBorderWidth > 0),
                        color: scatterPlotStyle.yAxisBorderColor,
                        width: scatterPlotStyle.yAxisBorderWidth,
                    },
                    ticks:{display: true}
                };
            }
            else if (dataset.title ===  "Markers") {
                /*
                // Inserting markers as chart dataset
                // Note: There doesn't seem to be a way to exclude a single dataset from mouse over detection (tooltip)
                chartDataset = {
                    showLine: false,
                    backgroundColor: scatterPlotStyle.markersColor,
                    pointRadius: scatterPlotStyle.markersRadius,
                    //pointHitRadius: 0,
                    data: dataset.data,
                    label: dataset.title
                };
                this.datasetsToPlot.push(chartDataset);*/

                // Adding markers as annotations
                dataset.data.forEach((point, idx) => {
                    this.chartOptions.plugins.annotation.annotations["marker" + (idx).toString()] = {
                        type: 'point',
                        xValue: point[0],
                        yValue: point[1],
                        backgroundColor: scatterPlotStyle.markersColor,
                        radius: scatterPlotStyle.markersRadius,
                        pointStyle: scatterPlotStyle.markersPointStyle,
                    }
                })
            } else if (dataset.title ===  "Labels") {
                // Adding label annotations
                dataset.data.forEach((point, idx) => {
                    this.chartOptions.plugins.annotation.annotations["label_" + (idx).toString()] = {
                        type: 'label',
                        xValue: point[0],
                        yValue: point[1], // ignoring y value (Fix position)
                        content: point[2],
                        position: 'start', // applied to vertical and horizontal position in the box
                        font: {
                            size: scatterPlotStyle.markersFontSize
                        }
                    }
                })
            }
            else if (dataset.title ===  "Image_markers") {
                // Inserting image markers as chart dataset
                chartDataset = {
                    showLine: false,
                    backgroundColor: scatterPlotStyle.imageMarkersColor,
                    pointRadius: scatterPlotStyle.imageMarkersRadius,
                    pointStyle: scatterPlotStyle.imageMarkersPointStyle,
                    pointBorderColor: scatterPlotStyle.pointBorderColor,
                    pointBackgroundColor: scatterPlotStyle.pointBackgroundColor,
                    pointBorderWidth: scatterPlotStyle.pointBorderWidth,
                    pointHoverRadius: scatterPlotStyle.imageMarkersRadius,
                    pointHoverBackgroundColor: scatterPlotStyle.pointHoverBackgroundColor,
                    pointHoverBorderColor: scatterPlotStyle.pointHoverBorderColor,
                    pointHitRadius: 2,
                    borderColor: scatterPlotStyle.pointBackgroundColor,
                    data: dataset.data,
                    label: dataset.title
                };
                this.datasetsToPlot.push(chartDataset);
            }
            else {
                console.warn("Unknown chart dataset: " + dataset.title);
            }
        }

        return (
            <div className="scatterPlot-Container">
                <Scatter data={{ datasets: this.datasetsToPlot }}
                         options={this.chartOptions}
                onMouseLeave={mouseLeaveEvent}/>
            </div>
        );
    }
}
